import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    User, Mail, Phone, GraduationCap, Briefcase,
    FileText, Upload, CheckCircle, ArrowRight, ArrowLeft,
    X, Plus, FileCheck, Shield, AlertCircle, Loader2
} from 'lucide-react';
import axios from 'axios';

const LecturerOnboarding = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [institution, setInstitution] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        // Step 1: Personal
        fullName: '',
        email: '',
        phoneNumber: '',
        profilePhoto: null,

        // Step 2: Qualifications
        qualification: 'Degree',
        experienceYears: 0,
        subjectsOfExpertise: [],
        newSubject: '',

        // Step 3: Bio
        bio: '',
        specialization: '',

        // Step 4: Documents
        idDocument: null,
        certificates: [],

        // Step 5: T&C
        agreedToTerms: false
    });

    useEffect(() => {
        const storedInstitution = localStorage.getItem('selectedInstitution');
        if (storedInstitution) {
            setInstitution(JSON.parse(storedInstitution));
        } else {
            // navigate('/select-institution');
            // For now, let's just set a placeholder if not found, 
            // but in production it should redirect
            setInstitution({ name: 'NPBC Online Academy', id: 1, primaryColor: '#2563eb' });
        }
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError('');
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (name === 'certificates') {
            const newFiles = Array.from(files);
            if (formData.certificates.length + newFiles.length > 5) {
                setError('You can upload a maximum of 5 certificates');
                return;
            }
            setFormData(prev => ({
                ...prev,
                certificates: [...prev.certificates, ...newFiles]
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));
        }
    };

    const removeCertificate = (index) => {
        setFormData(prev => ({
            ...prev,
            certificates: prev.certificates.filter((_, i) => i !== index)
        }));
    };

    const addSubject = () => {
        if (formData.newSubject.trim()) {
            setFormData(prev => ({
                ...prev,
                subjectsOfExpertise: [...prev.subjectsOfExpertise, formData.newSubject.trim()],
                newSubject: ''
            }));
        }
    };

    const removeSubject = (index) => {
        setFormData(prev => ({
            ...prev,
            subjectsOfExpertise: prev.subjectsOfExpertise.filter((_, i) => i !== index)
        }));
    };

    const validateStep = (step) => {
        switch (step) {
            case 1:
                if (!formData.fullName || !formData.email || !formData.phoneNumber) {
                    setError('Please fill in all personal details');
                    return false;
                }
                if (!/\S+@\S+\.\S+/.test(formData.email)) {
                    setError('Please enter a valid email address');
                    return false;
                }
                break;
            case 2:
                if (formData.subjectsOfExpertise.length === 0) {
                    setError('Please add at least one subject of expertise');
                    return false;
                }
                break;
            case 3:
                // Bio and Specialization are now optional
                break;
            case 4:
                // ID Document is now optional
                if (formData.certificates.length === 0) {
                    setError('Please upload at least one academic certificate');
                    return false;
                }
                break;
            case 5:
                if (!formData.agreedToTerms) {
                    setError('You must agree to the terms and conditions');
                    return false;
                }
                break;
            default:
                break;
        }
        return true;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => prev + 1);
            setError('');
        }
    };

    const prevStep = () => {
        setCurrentStep(prev => prev - 1);
        setError('');
    };

    const handleSubmit = async () => {
        if (!validateStep(5)) return;

        setLoading(true);
        const data = new FormData();
        data.append('institutionId', institution.id);
        data.append('fullName', formData.fullName);
        data.append('email', formData.email);
        data.append('phoneNumber', formData.phoneNumber);
        data.append('qualification', formData.qualification);
        data.append('experienceYears', formData.experienceYears);
        data.append('subjectsOfExpertise', JSON.stringify(formData.subjectsOfExpertise));
        data.append('bio', formData.bio);
        data.append('specialization', formData.specialization);

        if (formData.profilePhoto) data.append('profilePhoto', formData.profilePhoto);
        if (formData.idDocument) data.append('idDocument', formData.idDocument);
        formData.certificates.forEach(cert => {
            data.append('certificates', cert);
        });

        try {
            await axios.post('/lecturers/apply', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            navigate(`/lecturer/onboarding/pending?email=${formData.email}`);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Failed to submit application. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    const renderSplash = () => (
        <div className="space-y-12 animate-in fade-in zoom-in duration-700">
            {/* Hero Image Section */}
            <div className="relative h-64 md:h-80 rounded-3xl overflow-hidden shadow-2xl group">
                <img
                    src="/lecturer_onboarding_splash_1778841721027.png"
                    alt="Academic Excellence"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-8">
                    <h2 className="text-3xl md:text-4xl font-black text-white mb-2 leading-tight">
                        Join Our Distinguished <br />
                        <span className="text-blue-400">Academic Faculty</span>
                    </h2>
                    <p className="text-gray-200 text-sm md:text-base font-medium max-w-lg">
                        Shape the future of academic excellence and empower the next generation of leaders.
                    </p>
                </div>
            </div>

            {/* Content Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-2">
                <div className="space-y-4">
                    <div className="flex items-center gap-3 text-blue-600 mb-2">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <Shield className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-black uppercase tracking-tight">Our Mission</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed font-medium">
                        To provide world-class academic excellence, empowering leaders through innovative learning and holistic growth. We are committed to fostering an environment where faith and knowledge converge.
                    </p>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center gap-3 text-indigo-600 mb-2">
                        <div className="p-2 bg-indigo-50 rounded-lg">
                            <Briefcase className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-black uppercase tracking-tight">Your Impact</h3>
                    </div>
                    <ul className="space-y-3">
                        {[
                            { title: 'Excellence', desc: 'Maintain high academic standards' },
                            { title: 'Mentorship', desc: 'Guide students to reach their full potential' },
                            { title: 'Innovation', desc: 'Leverage modern tools for interactive teaching' }
                        ].map((item, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                                <div>
                                    <span className="block text-sm font-black text-gray-900 uppercase tracking-tighter">{item.title}</span>
                                    <span className="text-xs text-gray-500 font-medium">{item.desc}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Requirements Peek */}
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                        <h4 className="text-sm font-black text-gray-900 uppercase tracking-tight">Before you start</h4>
                        <p className="text-xs text-gray-500 font-medium">Ensure you have your academic certificates ready. ID upload is optional.</p>
                    </div>
                </div>
                <button
                    onClick={() => setCurrentStep(1)}
                    className="w-full md:w-auto bg-gray-900 hover:bg-black text-white font-black px-10 py-4 rounded-2xl flex items-center justify-center gap-3 shadow-xl shadow-gray-900/10 transition-all hover:scale-105 active:scale-95 group"
                >
                    BEGIN APPLICATION
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
            
            <div className="text-center pt-4">
                <Link 
                    to="/lecturer/onboarding/status" 
                    className="text-gray-400 hover:text-blue-600 text-xs font-black uppercase tracking-widest transition-all inline-flex items-center gap-2 group"
                >
                    Already applied? <span className="underline decoration-2 underline-offset-4 group-hover:text-blue-600">Check your status</span>
                </Link>
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                <p className="text-gray-500">Let's start with your contact details</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-600" /> Full Name
                    </label>
                    <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-600" /> Email Address
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-blue-600" /> Phone Number
                    </label>
                    <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        placeholder="+254 700 000 000"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Upload className="w-4 h-4 text-blue-600" /> Profile Photo (Optional)
                    </label>
                    <div className="relative group">
                        <input
                            type="file"
                            name="profilePhoto"
                            onChange={handleFileChange}
                            accept="image/*"
                            className="absolute inset-0 opacity-0 cursor-pointer z-10"
                        />
                        <div className="w-full bg-gray-50 border border-dashed border-gray-300 rounded-xl px-4 py-3 text-gray-900 flex items-center justify-center gap-3 group-hover:bg-gray-100 transition-all">
                            {formData.profilePhoto ? (
                                <span className="text-emerald-600 font-medium">{formData.profilePhoto.name}</span>
                            ) : (
                                <>
                                    <Upload className="w-5 h-5 text-gray-400" />
                                    <span className="text-gray-500">Click to upload photo</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Qualifications & Experience</h2>
                <p className="text-gray-500">Tell us about your professional background</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-blue-600" /> Highest Qualification
                    </label>
                    <select
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    >
                        <option value="Certificate">Certificate</option>
                        <option value="P1">P1</option>
                        <option value="Diploma">Diploma</option>
                        <option value="HND">HND</option>
                        <option value="Degree">Degree</option>
                        <option value="PGD">PGD</option>
                        <option value="Masters">Masters</option>
                        <option value="PhD">PhD</option>
                        <option value="Other">Other</option>
                    </select>
                </div>

                <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-blue-600" /> Years of Experience
                    </label>
                    <div className="flex items-center gap-4 bg-gray-50 border border-gray-200 rounded-xl px-2 py-1">
                        <button
                            onClick={() => setFormData(p => ({ ...p, experienceYears: Math.max(0, p.experienceYears - 1) }))}
                            className="w-10 h-10 rounded-lg hover:bg-gray-100 text-gray-600 flex items-center justify-center font-bold"
                        >-</button>
                        <span className="flex-1 text-center text-gray-900 font-bold text-lg">{formData.experienceYears}</span>
                        <button
                            onClick={() => setFormData(p => ({ ...p, experienceYears: p.experienceYears + 1 }))}
                            className="w-10 h-10 rounded-lg hover:bg-gray-100 text-gray-600 flex items-center justify-center font-bold"
                        >+</button>
                    </div>
                </div>
            </div>

            <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Plus className="w-4 h-4 text-blue-600" /> Subjects of Expertise
                </label>
                <div className="flex gap-2">
                    <input
                        type="text"
                        name="newSubject"
                        value={formData.newSubject}
                        onChange={handleChange}
                        onKeyPress={(e) => e.key === 'Enter' && addSubject()}
                        placeholder="Add a subject (e.g. Computer Science)"
                        className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                    <button
                        onClick={addSubject}
                        className="px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all"
                    >Add</button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                    {formData.subjectsOfExpertise.map((subject, index) => (
                        <span key={index} className="bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-2">
                            {subject}
                            <button onClick={() => removeSubject(index)} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Professional Profile</h2>
                <p className="text-gray-500">Share more about your expertise</p>
            </div>

            <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" /> Areas of Specialization (Optional)
                </label>
                <input
                    type="text"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleChange}
                    placeholder="e.g. Academic Writing, Technical Research, etc."
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
            </div>

            <div className="space-y-2">
                <div className="flex justify-between items-center">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" /> Professional Bio (Optional)
                    </label>
                    <span className="text-[10px] font-bold text-gray-400">
                        {formData.bio.length} / 500 characters
                    </span>
                </div>
                <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    maxLength={500}
                    placeholder="Briefly describe your teaching philosophy or professional journey..."
                    className="w-full h-40 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                />
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Document Verification</h2>
                <p className="text-gray-500">Upload your credentials for review</p>
            </div>

            <div className="space-y-4">
                <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-blue-600" /> National ID / Passport (Optional)
                    </label>
                    <div className="relative group">
                        <input
                            type="file"
                            name="idDocument"
                            onChange={handleFileChange}
                            accept=".pdf,image/*"
                            className="absolute inset-0 opacity-0 cursor-pointer z-10"
                        />
                        <div className={`w-full border-2 border-dashed rounded-xl px-4 py-8 text-center transition-all ${formData.idDocument ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}`}>
                            {formData.idDocument ? (
                                <div className="flex flex-col items-center gap-2">
                                    <FileCheck className="w-8 h-8 text-emerald-600" />
                                    <span className="text-emerald-700 font-bold">{formData.idDocument.name}</span>
                                    <span className="text-[10px] text-emerald-600 uppercase font-black">DOCUMENT ATTACHED</span>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <Upload className="w-8 h-8 text-gray-400" />
                                    <span className="text-gray-500 font-medium">Click to upload ID (Optional)</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <GraduationCap className="w-4 h-4 text-blue-600" /> Academic Certificates (Min 1, Max 5)
                        </label>
                        <span className="text-[10px] font-bold text-gray-400">{formData.certificates.length} / 5</span>
                    </div>
                    <div className="relative group">
                        <input
                            type="file"
                            name="certificates"
                            multiple
                            onChange={handleFileChange}
                            accept=".pdf,image/*"
                            className="absolute inset-0 opacity-0 cursor-pointer z-10"
                            disabled={formData.certificates.length >= 5}
                        />
                        <div className="w-full bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl px-4 py-6 text-center hover:bg-gray-100 transition-all">
                            <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                            <span className="text-gray-500 text-sm font-medium">Click to add certificates</span>
                        </div>
                    </div>

                    <div className="space-y-2 mt-4">
                        {formData.certificates.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-white border border-gray-100 rounded-lg p-3 group animate-in slide-in-from-bottom duration-300">
                                <div className="flex items-center gap-3">
                                    <FileText className="w-4 h-4 text-blue-500" />
                                    <span className="text-xs text-gray-700 font-medium truncate max-w-[200px]">{file.name}</span>
                                </div>
                                <button onClick={() => removeCertificate(index)} className="p-1.5 hover:bg-red-50 text-red-500 hover:text-white rounded-md transition-colors">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStep5 = () => (
        <div className="space-y-6 animate-in fade-in slide-in-from-right duration-500">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Review & Submit</h2>
                <p className="text-gray-500">Verify your application for {institution?.name}</p>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-gray-400 text-[10px] uppercase font-black tracking-wider">Full Name</p>
                        <p className="text-gray-900 font-bold">{formData.fullName}</p>
                    </div>
                    <div>
                        <p className="text-gray-400 text-[10px] uppercase font-black tracking-wider">Email</p>
                        <p className="text-gray-900 font-bold">{formData.email}</p>
                    </div>
                    <div>
                        <p className="text-gray-400 text-[10px] uppercase font-black tracking-wider">Qualification</p>
                        <p className="text-gray-900 font-bold">{formData.qualification}</p>
                    </div>
                    <div>
                        <p className="text-gray-400 text-[10px] uppercase font-black tracking-wider">Experience</p>
                        <p className="text-gray-900 font-bold">{formData.experienceYears} Years</p>
                    </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                    <p className="text-gray-400 text-[10px] uppercase font-black tracking-wider mb-2">Expertise</p>
                    <div className="flex flex-wrap gap-2">
                        {formData.subjectsOfExpertise.map((s, i) => (
                            <span key={i} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold border border-blue-100">
                                {s}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                    <p className="text-gray-400 text-[10px] uppercase font-black tracking-wider mb-1">Documents Attached</p>
                    <p className="text-xs text-gray-600">{formData.idDocument ? '1 ID Document' : 'No ID Document'} • {formData.certificates.length} Certificates</p>
                </div>
            </div>

            <div className="flex gap-3 items-start p-4 bg-blue-50 border border-blue-100 rounded-xl cursor-pointer" onClick={() => setFormData(p => ({ ...p, agreedToTerms: !p.agreedToTerms }))}>
                <div className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${formData.agreedToTerms ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
                    {formData.agreedToTerms && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <p className="text-xs text-gray-700 leading-relaxed">
                    I confirm that all information provided is accurate and I agree to the <span className="text-blue-600 underline font-bold">Lecturer Onboarding Policy</span> and <span className="text-blue-600 underline font-bold">Code of Conduct</span>.
                </p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 flex flex-col items-center justify-center p-4 md:p-8 font-sans selection:bg-blue-500 selection:text-white">
            <div className="w-full max-w-2xl relative z-10">
                {/* Institution Branding */}
                <div className="text-center mb-8 flex flex-col items-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-xl mb-4 transform hover:rotate-6 transition-transform">
                        <GraduationCap className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight uppercase">
                        {institution?.name || 'INTUIT LMS'}
                    </h1>
                    <div className="bg-blue-50 px-4 py-1 rounded-full border border-blue-100 mt-2">
                        <span className="text-xs font-black text-blue-600 uppercase tracking-widest">Lecturer Onboarding</span>
                    </div>
                </div>

                {/* Progress Bar */}
                {currentStep > 0 && (
                    <div className="mb-10 px-4 animate-in fade-in duration-500">
                        <div className="flex justify-between mb-3">
                            {[1, 2, 3, 4, 5].map((step) => (
                                <div
                                    key={step}
                                    className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black transition-all duration-500 border-2 ${currentStep === step
                                        ? 'bg-blue-600 border-blue-600 text-white shadow-lg scale-110'
                                        : currentStep > step
                                            ? 'bg-emerald-50 border-emerald-100 text-emerald-600'
                                            : 'bg-white border-gray-100 text-gray-300'
                                        }`}
                                >
                                    {currentStep > step ? <CheckCircle className="w-5 h-5" /> : step}
                                </div>
                            ))}
                        </div>
                        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-700 ease-out"
                                style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                {/* Main Form Card */}
                <div className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden p-8 md:p-10">
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 animate-in shake duration-500">
                            <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                            <p className="text-sm text-red-700 font-medium">{error}</p>
                        </div>
                    )}

                    <div className={currentStep === 0 ? "min-h-[500px]" : "min-h-[400px]"}>
                        {currentStep === 0 && renderSplash()}
                        {currentStep === 1 && renderStep1()}
                        {currentStep === 2 && renderStep2()}
                        {currentStep === 3 && renderStep3()}
                        {currentStep === 4 && renderStep4()}
                        {currentStep === 5 && renderStep5()}
                    </div>

                    {currentStep > 0 && (
                        <div className="mt-12 flex justify-between items-center pt-8 border-t border-gray-100 animate-in fade-in duration-500">
                            {currentStep > 1 ? (
                                <button
                                    onClick={prevStep}
                                    className="flex items-center gap-2 text-gray-400 hover:text-gray-600 font-bold transition-all px-4 py-2"
                                >
                                    <ArrowLeft className="w-5 h-5" /> Back
                                </button>
                            ) : (
                                <Link to="/register" className="text-gray-400 hover:text-gray-600 text-sm font-bold flex items-center gap-2">
                                    <X className="w-4 h-4" /> Cancel
                                </Link>
                            )}

                            {currentStep < 5 ? (
                                <button
                                    onClick={nextStep}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3.5 rounded-2xl flex items-center gap-3 shadow-lg shadow-blue-600/20 transition-all hover:translate-x-1 group"
                                >
                                    Continue <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white font-black px-10 py-4 rounded-2xl flex items-center gap-3 shadow-lg shadow-emerald-600/20 transition-all"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            Submit Application <CheckCircle className="w-5 h-5" />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="mt-8 text-center">
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">
                        {new Date().getFullYear()} &copy; {institution?.name}. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LecturerOnboarding;
